import React, { useState, useEffect } from "react";
import { Box, Tab, Avatar, Tooltip, CircularProgress } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import RxTerminView from "../../../components/PatientOverview/RxInfo/RxTerminView";
import ServicesTerminView from "../../../components/PatientOverview/RxInfo/ServicesTerminView";
import { TerminViewConstants } from "../../../components/PatientOverview/TerminView";
import { useAllRxIds } from "../../../stores/Rx";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import useStore from "../../../helpers/useStore";
import { observer } from "mobx-react";

interface PatientRxTabsProps {
  patientId: number;
}

const PatientRxTabs: React.FC<PatientRxTabsProps> = observer(({ patientId }) => {
  let tabValue = "Services";

  // Fetch all Rx IDs for the patient
  const { data: rxIds = [], isLoading, error } = useAllRxIds(patientId, true); // activeOnly = true

  // Get selection state from CalendarStore:
  const CalendarStore = useStore(CalendarContext);
  const selectedRxId = CalendarStore.selectedRxId;
  const serviceSelected = CalendarStore.serviceSelected;

  // Create tabs list with Rx IDs and Services
  const allTabIds = [...rxIds.map((rx) => rx.id), "Services"];

  if (rxIds.length > 0) {
    // Check if service tab is explicitly selected
    if (serviceSelected) {
      tabValue = "Services";
    } else if (selectedRxId && allTabIds.includes(selectedRxId)) {
      // Use selectedRxId from store if provided and exists in tabs
      tabValue = selectedRxId;
    } else {
      // Default to first Rx tab if no specific selection
      tabValue = allTabIds[0];
    }
  }

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    // console.debug("Tab changed to:", newValue, "from:", tabValue);
    
    if (newValue === "Services") {
      // Service tab selected - set serviceSelected to true and clear rxId
      CalendarStore.setServiceSelected(true);
    } else {
      // Rx tab selected - set the rxId and clear serviceSelected
      CalendarStore.setSelectedRxId(newValue);
    }
  };

  if (patientId === undefined) {
    return null;
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <span>Fehler beim Laden der Rx Daten</span>
      </Box>
    );
  }

  // console.debug("Rendering PatientRxTabs with tabValue:", tabValue, "and allTabIds:", allTabIds);

  return (
    <Box marginTop={1} marginBottom={8}>
      <TabContext value={tabValue}>
        {/* Dense chrome: MUI's stock Tab is 48px tall and sized for a settings
            page. In a 320px drawer the tab strip is navigation, not content. */}
        <Box sx={{ borderBottom: "1px solid var(--ink-line)" }}>
          <TabList
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 32,
              "& .MuiTab-root": {
                minHeight: 32,
                padding: "4px 10px",
                fontSize: "var(--text-title)",
                fontWeight: 600,
                textTransform: "none",
                minWidth: 0,
              },
            }}
          >
            {allTabIds.map((tabId: string, index: number) => {
              // Find the Rx data for this tab to display the rxNumber
              const rxData = rxIds.find((rx) => rx.id === tabId);
              const displayLabel = tabId === "Services" ? "Services" : rxData?.rxNumber || tabId;

              return (
                <Tab
                  value={tabId}
                  key={tabId}
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {/* TODO: Add status indicator when available in RxIdDto or fetch from separate endpoint */}
                      {/* <Tooltip title={tabId === "Services" ? "Services" : `Rx ${displayLabel}`}>
                        <Avatar
                          sx={{
                            width: 12,
                            height: 12,
                            bgcolor: "primary.main", // Default color, can be updated when status is available
                            "& > *": { display: "none" },
                          }}
                        >
                          <React.Fragment></React.Fragment>
                        </Avatar>
                      </Tooltip> */}
                      {displayLabel}
                    </Box>
                  }
                />
              );
            })}
          </TabList>
        </Box>
        {allTabIds.map((tabId: string, index: number) => {
          // Find the Rx data for this tab to get the rxNumber
          const rxData = rxIds.find((rx) => rx.id === tabId);
          // console.debug("Rendering TabPanel for tabId:", tabId, "with rxData:", rxData);
          return (
            <TabPanel
              key={tabId}
              value={tabId}
              // TabPanel ships with 24px of padding, which spends 48px of a
              // 320px drawer on nothing. cardBackground is --surface-sunken:
              // this is the container rung the white cards sit on.
              sx={{ backgroundColor: mobiliTheme.cardBackground, padding: "6px" }}
            >
              {tabId === "Services" ? (
                // Use ServicesTerminView for Services tab
                // MD-724: "Storniert/Ungeplant" starts ON here. A sale is paid
                // up front, so an unplanned sales appointment is owed to the
                // patient — hiding it is how a paid appointment went missing.
                <ServicesTerminView
                  patientId={patientId}
                  dataRefreshFlag={undefined}
                  setDataRefreshFlag={undefined}
                  isView={TerminViewConstants.RX_VIEW}
                  includeAllAppointments={true}
                />
              ) : (
                // Use RxTerminView for Rx tabs - pass rxId, not rxNumber
                <RxTerminView displayRxInfo={true} rxId={rxData?.id || tabId} patientId={patientId} />
              )}
            </TabPanel>
          );
        })}
      </TabContext>
    </Box>
  );
});

export default PatientRxTabs;
